import { c as _c } from "react/compiler-runtime";
import { IcoBook16, IcoBuilding16, IcoRobot16, IcoUser16 } from '@onefootprint/icons';
import { LoadingSpinner } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MentionItem } from '../mention-extension/types';
import { getEntityLabel } from '../mention-extension/utils';
export type MentionListHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
  setLoading: (loading: boolean) => void;
};
type MentionListProps = {
  items: MentionItem[];
  query: string;
  isLoading?: boolean;
  onSelect: (item: MentionItem) => void;
};
const PopupShell = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="w-fit min-w-[260px] max-w-[calc(100vw-32px)] md:max-w-[480px] bg-elevated border border-tertiary rounded-default shadow-md p-1 max-h-[320px] overflow-y-auto flex flex-col gap-3">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const itemIdAndLabel = (item: MentionItem): {
  id: string;
  label: string;
} => {
  switch (item.type) {
    case 'agent':
      return {
        id: item.agent.id,
        label: item.agent.name
      };
    case 'playbook':
      return {
        id: item.playbook.id,
        label: item.playbook.publishedVersion.name
      };
    default:
      return {
        id: item.entity.id,
        label: getEntityLabel(item.entity)
      };
  }
};
const MentionList = forwardRef<MentionListHandle, MentionListProps>((t0, ref) => {
  const $ = _c(34);
  const {
    items,
    query,
    isLoading,
    onSelect
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [internalLoading, setInternalLoading] = useState(false);
  const containerRef = useRef(null);
  const isKeyboardNav = useRef(false);
  const effectiveLoading = isLoading || internalLoading;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setInternalLoading(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== items) {
    t2 = [items];
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  const grouped = {
    agents: items.flatMap(_temp),
    playbooks: items.flatMap(_temp2),
    users: items.flatMap(_temp3),
    businesses: items.flatMap(_temp4)
  };
  const flatList = [...grouped.agents, ...grouped.playbooks, ...grouped.users, ...grouped.businesses];
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setSelectedIndex(0);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== items) {
    t4 = [items];
    $[4] = items;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== selectedIndex) {
    t5 = () => {
      if (!isKeyboardNav.current) {
        return;
      }
      isKeyboardNav.current = false;
      const el = containerRef.current?.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | undefined;
      el?.scrollIntoView({
        block: "nearest"
      });
    };
    t6 = [selectedIndex];
    $[6] = selectedIndex;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  useImperativeHandle(ref, () => ({
    onKeyDown: event => {
      if (event.key === "ArrowUp") {
        isKeyboardNav.current = true;
        setSelectedIndex(prev => prev <= 0 ? flatList.length - 1 : prev - 1);
        return true;
      }
      if (event.key === "ArrowDown") {
        isKeyboardNav.current = true;
        setSelectedIndex(prev_0 => (prev_0 + 1) % Math.max(flatList.length, 1));
        return true;
      }
      if (event.key === "Enter") {
        const item_3 = flatList[selectedIndex];
        if (item_3) {
          onSelect(item_3);
          return true;
        }
        return false;
      }
      return false;
    },
    setLoading: loading => setInternalLoading(loading)
  }));
  if (effectiveLoading && flatList.length === 0) {
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <LoadingSpinner size="compact" />;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t) {
      t8 = t(_temp5);
      $[10] = t;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== t8) {
      t9 = <div className="w-fit min-w-[260px] bg-elevated border border-tertiary rounded-default shadow-md p-1"><div className="flex items-center gap-2 px-2 py-1.5 text-body-3 text-tertiary">{t7}<span className="text-shimmer">{t8}</span></div></div>;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  if (!query.trim()) {
    let t7;
    if ($[14] !== t) {
      t7 = t(_temp6);
      $[14] = t;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t7) {
      t8 = <span className="pointer-events-none select-none whitespace-nowrap text-body-3 text-quaternary">{t7}</span>;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  if (flatList.length === 0) {
    let t7;
    if ($[18] !== query || $[19] !== t) {
      t7 = t(_temp7, {
        query
      });
      $[18] = query;
      $[19] = t;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== t7) {
      t8 = <div className="w-fit min-w-[260px] bg-elevated border border-tertiary rounded-default shadow-md p-1"><div className="px-2 py-1.5 text-body-3 text-tertiary">{t7}</div></div>;
      $[21] = t7;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    return t8;
  }
  let t7;
  if ($[23] !== onSelect || $[24] !== selectedIndex) {
    t7 = (item_4, index, Icon) => {
      const isSelected = index === selectedIndex;
      const {
        id,
        label
      } = itemIdAndLabel(item_4);
      return <button key={id} type="button" data-index={index} className={cx("flex items-center gap-2 w-full px-2 py-1.5 rounded-default text-left cursor-pointer", isSelected ? "bg-(--dropdown-hover-bg)" : "hover:bg-(--dropdown-hover-bg)")} onClick={() => onSelect(item_4)} onMouseEnter={() => setSelectedIndex(index)}><span className="shrink-0 flex items-center text-tertiary"><Icon /></span><span className="flex flex-col gap-0.5 min-w-0 flex-1"><span className="text-body-3 text-primary wrap-break-word">{label}</span><span className="text-snippet-3 text-tertiary font-mono break-all">{id}</span></span></button>;
    };
    $[23] = onSelect;
    $[24] = selectedIndex;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const renderRow = t7;
  const playbooksOffset = grouped.agents.length;
  const usersOffset = playbooksOffset + grouped.playbooks.length;
  const businessesOffset = usersOffset + grouped.users.length;
  const T0 = PopupShell;
  const t8 = "flex flex-col gap-3";
  const t9 = grouped.agents.length > 0 && <div className="flex flex-col gap-0.5"><div className="px-2 pt-2 pb-1 text-tertiary text-caption-3">{t(_temp8)}</div>{grouped.agents.map((item_5, i) => renderRow(item_5, i, IcoRobot16))}</div>;
  const t10 = grouped.playbooks.length > 0 && <div className="flex flex-col gap-0.5"><div className="px-2 pt-2 pb-1 text-tertiary text-caption-3">{t(_temp9)}</div>{grouped.playbooks.map((item_6, i_0) => renderRow(item_6, playbooksOffset + i_0, IcoBook16))}</div>;
  const t11 = grouped.users.length > 0 && <div className="flex flex-col gap-0.5"><div className="px-2 pt-2 pb-1 text-tertiary text-caption-3">{t(_temp10)}</div>{grouped.users.map((item_7, i_1) => renderRow(item_7, usersOffset + i_1, IcoUser16))}</div>;
  const t12 = grouped.businesses.length > 0 && <div className="flex flex-col gap-0.5"><div className="px-2 pt-2 pb-1 text-tertiary text-caption-3">{t(_temp11)}</div>{grouped.businesses.map((item_8, i_2) => renderRow(item_8, businessesOffset + i_2, IcoBuilding16))}</div>;
  let t13;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t9) {
    t13 = <div ref={containerRef} className={t8}>{t9}{t10}{t11}{t12}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== T0 || $[32] !== t13) {
    t14 = <T0>{t13}</T0>;
    $[31] = T0;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
});
export default MentionList;
function _temp(item) {
  return item.type === "agent" ? [item] : [];
}
function _temp2(item_0) {
  return item_0.type === "playbook" ? [item_0] : [];
}
function _temp3(item_1) {
  return item_1.type === "entity" && item_1.entity.kind !== "business" ? [item_1] : [];
}
function _temp4(item_2) {
  return item_2.type === "entity" && item_2.entity.kind === "business" ? [item_2] : [];
}
function _temp5($) {
  return $.chat.mentions.searching;
}
function _temp6($_0) {
  return $_0.chat.mentions.hint;
}
function _temp7($_1) {
  return $_1.chat.mentions.noMatches;
}
function _temp8($_2) {
  return $_2.chat.mentions.groups.agents;
}
function _temp9($_3) {
  return $_3.chat.mentions.groups.playbooks;
}
function _temp10($_4) {
  return $_4.chat.mentions.groups.users;
}
function _temp11($_5) {
  return $_5.chat.mentions.groups.businesses;
}